import {
  add,
  addDays,
  addHours,
  addMinutes,
  addMonths,
  addSeconds,
  addWeeks,
  addYears,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInMonths,
  differenceInSeconds,
  differenceInWeeks,
  differenceInYears,
  isAfter,
  isBefore,
  isEqual,
  startOfDay,
  startOfHour,
  startOfMinute,
  startOfMonth,
  startOfSecond,
  startOfWeek,
  startOfYear,
  sub
} from 'date-fns';

import { assertUnreachable } from 'cadenza/utils/custom-error';
import type { TickUnit } from 'cadenza/conditions/model/tick-unit';
import type { StepsConfig } from 'cadenza/conditions/date-time-condition/steps-config';
import { StepDirection } from 'cadenza/conditions/date-time-condition/step-direction';
import type { RelativeDateIntervalUnit } from 'cadenza/relative-date-time/relative-date-interval-unit';
import type { RelativeDateTime } from 'cadenza/relative-date-time/relative-date-time';
import type { RelativeDateTimeInterval } from 'cadenza/relative-date-time/relative-date-time-interval';
import { isClassicRelativeDateTime } from 'cadenza/relative-date-time/is-classic-relative-date-time';
import type { DateConditionConfigurationDto } from 'cadenza/workbook/model/condition-dto';
import type { Settings } from 'cadenza/workbook/workbook-settings-util';
import { getSetting } from 'cadenza/workbook/workbook-settings-util';
import { CONDITION_SETTINGS } from 'cadenza/workbook/condition-edit-dialog/condition-settings';

const RELATIVE_DATE_INTERVAL_UNIT_TO_TICK_UNIT: Record<RelativeDateIntervalUnit, TickUnit> = {
  SECOND: 'seconds',
  MINUTE: 'minutes',
  HOUR: 'hours',
  DAY: 'days',
  WEEK: 'weeks',
  MONTH: 'months',
  QUARTER: 'months',
  YEAR: 'years'
} as const;

/**
 * Checks whether the steps configuration is compatible with the condition value range and adjust
 * the steps configuration to fit the values.
 * If possible, only the window size is adjusted and the step unit is left as is, but if the value
 * range does not fit exactly into a multiple of the unit, the unit is adjusted, too (set to the
 * fallback value).
 *
 * @param startDate - date range start value
 * @param endDate - date range end value
 * @param currentStepsConfig - the current steps configuration
 * @param smallestStepUnit - step unit to fall back to if the passed date range is not compatible with the step unit in currentStepsConfig
 * @return - the adapted steps config (can be equal to the one passed in if no changes were necessary)
 */
export function adaptStepsConfigToConditionValue (startDate: Date, endDate: Date, currentStepsConfig: StepsConfig, smallestStepUnit: TickUnit) {
  // We ignore more fine-grained date/time components than the smallestStepUnit
  startDate = roundToTickUnit(startDate, smallestStepUnit);
  const roundedEndDate = roundToTickUnit(endDate, smallestStepUnit);
  const adjustedEndDate = addTickUnits(roundedEndDate, 1, smallestStepUnit);

  const rangeLengthInStepUnit = differenceInTickUnit(startDate, adjustedEndDate, currentStepsConfig.unit);
  const lenghInStepUnitsCoversFullRange =
    addTickUnits(startDate, rangeLengthInStepUnit, currentStepsConfig.unit).getTime()
      === adjustedEndDate.getTime();

  if (lenghInStepUnitsCoversFullRange) {
    return {
      ...currentStepsConfig,
      timeWindow: rangeLengthInStepUnit
    };
  } else {
    const timeWindow = differenceInTickUnit(startDate, adjustedEndDate, smallestStepUnit);
    return {
      ...currentStepsConfig,
      unit: smallestStepUnit,
      timeWindow
    };
  }
}

/**
 * In case the steps config changed, it might be necessary to adapt the condition value to fit the new steps config.
 *
 * @return {object} - the adapted date range (can be equal to the one passed in if no changes were necessary)
 */
export function adaptConditionValueToStepsConfig (dateConditionRangeValue: { start: Date; end: Date }, stepsConfig: StepsConfig) {
  if (dateConditionRangeValue) {
    const newEndDate = addTickUnits(
      new Date(dateConditionRangeValue.start),
      stepsConfig.timeWindow,
      stepsConfig.unit);
    return {
      ...dateConditionRangeValue,
      end: newEndDate.toISOString()
    };
  } else {
    return { };
  }
}

/**
 * Calculates the difference of two dates in the specified unit. Always returns an integer.
 * Fractions of the specified unit that additionally fit into the date range are discarded.
 *
 * @param start - start date
 * @param end - end date
 * @param tickUnit - unit
 * @return the rounded-down difference between start and end, in the specified unit
 */
export function differenceInTickUnit (start: Date, end: Date, tickUnit: TickUnit) {
  switch (tickUnit) {
    case 'years':   return differenceInYears(end, start);
    case 'months':  return differenceInMonths(end, start);
    case 'weeks':   return differenceInWeeks(end, start);
    case 'days':    return differenceInDays(end, start);
    case 'hours':   return differenceInHours(end, start);
    case 'minutes': return differenceInMinutes(end, start);
    case 'seconds': return differenceInSeconds(end, start);
  }
  assertUnreachable(tickUnit);
}

/**
 * Rounds the given date to the given date/time unit
 *
 * @param date - date to round
 * @param tickUnit - unit to round to
 * @return rounded date
 */
export function roundToTickUnit (date: Date, tickUnit: TickUnit) {
  switch (tickUnit) {
    case 'years':   return startOfYear(date);
    case 'months':  return startOfMonth(date);
    case 'weeks':   return startOfWeek(date);
    case 'days':    return startOfDay(date);
    case 'hours':   return startOfHour(date);
    case 'minutes': return startOfMinute(date);
    case 'seconds': return startOfSecond(date);
  }
  assertUnreachable(tickUnit);
}

/**
 * Adds time to a date.
 *
 * @param date - base date
 * @param number - number to be added
 * @param tickUnit - unit the number is interpreted as
 * @return base date plus added time
 */
export function addTickUnits (date: Date, number: number, tickUnit: TickUnit) {
  switch (tickUnit) {
    case 'years':   return addYears(date, number);
    case 'months':  return addMonths(date, number);
    case 'weeks':   return addWeeks(date, number);
    case 'days':    return addDays(date, number);
    case 'hours':   return addHours(date, number);
    case 'minutes': return addMinutes(date, number);
    case 'seconds': return addSeconds(date, number);
  }
  assertUnreachable(tickUnit);
}

export function stepsConfigEquals (stepConfig1: StepsConfig, stepConfig2: StepsConfig) {
  if ((stepConfig1 ?? null) === null) {
    return stepConfig1 === stepConfig2;
  }

  return stepConfig1.timeWindow === stepConfig2.timeWindow
    && stepConfig1.unit === stepConfig2.unit
    && stepConfig1.overlap === stepConfig2.overlap;
}

export function dateConditionRangeValueEquals (conditionValue1: { start: Date; end: Date }, conditionValue2: { start: Date; end: Date }) {
  if ((conditionValue1 ?? null) === null) {
    return conditionValue1 === conditionValue2;
  }

  return conditionValue1.start === conditionValue2.start
    && conditionValue1.end === conditionValue2.end;
}

/**
 * Creates a StepsConfig object from an object containing condition settings (keys from CONDITION_SETTINGS).
 *
 * @return the steps config
 */
export function toStepsConfig (conditionSettings: Settings): StepsConfig {
  return {
    timeWindow: getSetting<number>(conditionSettings, CONDITION_SETTINGS.DATE_CONDITION_INTERVAL_LENGTH)!,
    unit: getSetting<TickUnit>(conditionSettings, CONDITION_SETTINGS.DATE_CONDITION_INTERVAL_UNIT)!,
    overlap: getSetting<number>(conditionSettings, CONDITION_SETTINGS.DATE_CONDITION_INTERVAL_OVERLAP_LENGTH)!
  };
}

export function toConditionSettings (stepsConfig: StepsConfig) {
  return {
    [CONDITION_SETTINGS.DATE_CONDITION_INTERVAL_LENGTH]: stepsConfig.timeWindow,
    [CONDITION_SETTINGS.DATE_CONDITION_INTERVAL_UNIT]: stepsConfig.unit,
    [CONDITION_SETTINGS.DATE_CONDITION_INTERVAL_OVERLAP_LENGTH]: stepsConfig.overlap
  };
}

export function mostFineGrainedDateFormatTickUnit (format = ''): TickUnit | undefined {
  if (format.includes('s')) {
    return 'seconds';
  }
  if (format.includes('m')) {
    return 'minutes';
  }
  if (format.includes('H')) {
    return 'hours';
  }
  if (format.includes('d')) {
    return 'days';
  }
  if (format.includes('M')) {
    return 'months';
  }
  // quarters
  if (format.includes('q')) {
    return 'months';
  }
  if (format.includes('y')) {
    return 'years';
  }
}

export function mostFineGrainedTickUnit (dateConfiguration: DateConditionConfigurationDto): TickUnit {
  if (dateConfiguration.secondEnabled) {
    return 'seconds';
  }
  if (dateConfiguration.minuteEnabled) {
    return 'minutes';
  }
  if (dateConfiguration.hourEnabled) {
    return 'hours';
  }
  if (dateConfiguration.dayEnabled) {
    return 'days';
  }
  if (dateConfiguration.monthEnabled) {
    return 'months';
  }
  if (dateConfiguration.quarterEnabled) {
    return 'months';
  }
  if (dateConfiguration.yearEnabled) {
    return 'years';
  }
  throw new Error('At least one time unit must be enabled');
}

/**
 * Creates a steps configuration according to the relative date that is passed.
 *
 * @param relativeDate - Relative date to create the steps config from.
 */
export function stepsConfigFromRelativeDate (relativeDate: RelativeDateTimeInterval | RelativeDateTime): StepsConfig {
  const multiplier = relativeDate.unit === 'QUARTER' ? 3 : 1;
  const amount = isClassicRelativeDateTime(relativeDate)
    ? relativeDate.amount + 1
    : (relativeDate.to - relativeDate.from) + 1;
  return {
    unit: RELATIVE_DATE_INTERVAL_UNIT_TO_TICK_UNIT[relativeDate.unit],
    timeWindow: amount * multiplier,
    overlap: 0
  };
}

interface CalculateSteppedSliderRangeResult {
  /** Slider range that is possibly non-linear to use with NoUiSlider @see {@link https://refreshless.com/nouislider/examples/#section-non-linear}. */
  sliderRange: object;
  /** The maximum tick value that is applicable, slider can't be dragged beyond that. */
  lastLowerHandleTickValue: number;
}

/**
 * In stepping mode the slider can only be moved to certain values on the slider track. Therefore
 * the allowed values are calculated and returned as needed by NoUiSlider. Also the maximum last
 * tick value for the slider is returned.
 *
 * @param stepSize - Time window step size.
 * @param tickUnit - Smallest available date unit granularity.
 * @param lastTickValue - End of date range as tick value.
 * @param dateRange - Maximum available date range.
 * @param stepsUnit - Time window date unit.
 * @param conditionStartValue - Current condition start date string, needed to calculate a slider range that includes that value.
 * @param isRelativeDate - Whether the conditionValue is derived from a relative date.
 * @return {CalculateSteppedSliderRangeResult}
 */
export function calculateSteppedSliderRange (
  stepSize: number,
  tickUnit: TickUnit,
  lastTickValue: number,
  dateRange: { lowerBound: Date; upperBound: Date },
  stepsUnit: TickUnit,
  conditionStartValue: Date,
  isRelativeDate: boolean
): CalculateSteppedSliderRangeResult {
  conditionStartValue = conditionStartValue ?? dateRange.lowerBound;
  conditionStartValue = isRelativeDate
    ? relativeConditionValueToStartDate(conditionStartValue, dateRange, stepsUnit, stepSize)
    : new Date(conditionStartValue);
  stepSize = Math.abs(stepSize); // Negative stepSize would break the slider.
  const adjustedDateRange = {
    lowerBound: calculateSteppedSliderRangeStart(stepsUnit, stepSize, conditionStartValue ?? dateRange.lowerBound, dateRange.lowerBound),
    upperBound: dateRange.upperBound
  };
  const sliderRange: Record<string, number[]> = {
    max: [ lastTickValue ]
  };
  const getNextPossibleIndex = (range: Date[], index: number, desiredStepSize: number) => {
    const desiredIndex = index + desiredStepSize;
    if (range.length > desiredIndex) {
      return desiredIndex;
    } else if (range.length > index) {
      return range.length - 1;
    } else {
      return index;
    }
  };

  const offset = differenceInTickUnit(dateRange.lowerBound, adjustedDateRange.lowerBound, tickUnit);
  const slicedDateRange = sliceDateRangeByTickUnit(adjustedDateRange, stepsUnit);
  let tickValue: number;
  slicedDateRange.forEach((date, index) => {
    if (index % stepSize === 0) {
      tickValue = differenceInTickUnit(dateRange.lowerBound, date, tickUnit);
      const tickSliderPosition = (tickValue - offset) * 100 / (sliderRange.max[0] - offset);
      // For more information on how noUi-Slider supports stepping with non-linear sliders see
      // https://refreshless.com/nouislider/slider-values/#section-non-linear-step
      if (index < slicedDateRange.length) {
        const nextIndex = getNextPossibleIndex(slicedDateRange, index, stepSize);
        const nextValue = differenceInTickUnit(dateRange.lowerBound, slicedDateRange[nextIndex], tickUnit);
        if (index === 0) {
          // Special case lowest value in range: field must be named 'min'.
          sliderRange.min = [ offset, nextValue - tickValue ];
        } else {
          // For each step the slider has, the following step size is also set. That's the condition
          // that must be met for a non-linear slider to be in stepping mode.
          sliderRange[`${tickSliderPosition}%`] = [ tickValue, nextValue - tickValue ];
        }
      }
    }
  });
  return { sliderRange, lastLowerHandleTickValue: tickValue! };
}

/**
 * Determines if stepping on the slider is possible for the given direction when a relative date is set.
 *
 * @param direction - Which stepping direction should be tested: previous or next.
 * @param sliderTickValue - The relative date to test in tick values.
 * @param minRangeLimit - The lower range limit in tick values.
 * @param maxRangeLimit - The upper range limit in tick values.
 * @return If stepping in the given direction is possible.
 */
export function canStepFromRelativeDate (direction: StepDirection, sliderTickValue: number, minRangeLimit: number, maxRangeLimit: number) {
  if (sliderTickValue > minRangeLimit && sliderTickValue < maxRangeLimit) {
    return true;
  }
  return (direction === StepDirection.PREVIOUS && sliderTickValue > maxRangeLimit)
    || (direction === StepDirection.NEXT && sliderTickValue < minRangeLimit);
}

// Calculate the start date if the relative date is out of date range bounds.
function relativeConditionValueToStartDate (
  conditionStartValue: Date,
  dateRange: { lowerBound: Date; upperBound: Date },
  stepsUnit: TickUnit,
  stepSize: number) {
  const lowerBound = dateRange.lowerBound;
  const upperBound = dateRange.upperBound;
  let start = new Date(conditionStartValue);

  if ((isAfter(start, lowerBound) || isEqual(start, lowerBound))
    && (isBefore(start, upperBound) || isEqual(start, upperBound))) {
    return start; // start is within date range
  }

  // step forward towards the allowed range
  if (isBefore(start, lowerBound)) {
    while (isBefore(start, lowerBound)) {
      start = add(start, { [stepsUnit]: stepSize });
    }
    if (isAfter(start, upperBound)) {
      return lowerBound; // No match in the given date range, return lower bound.
    }
    return start;
  }

  // step backwards towards the allowed range
  if (isAfter(start, upperBound)) {
    while (isAfter(start, upperBound)) {
      start = add(start, { [stepsUnit]: -stepSize });
    }
    if (isBefore(start, lowerBound)) {
      return lowerBound; // No match in the given date range, return lower bound.
    }

    return start;
  }

  return lowerBound; // Fallback: return lower bound.
}

function calculateSteppedSliderRangeStart (stepsUnit: TickUnit, stepSize: number, conditionStartValue: Date, dateRangeLowerBound: Date) {
  let rangeStart = new Date(conditionStartValue);
  let newRangeStart = rangeStart;
  while (isAfter(newRangeStart, dateRangeLowerBound)) {
    newRangeStart = sub(rangeStart, { [stepsUnit]: stepSize });
    if (!isBefore(newRangeStart, dateRangeLowerBound)) {
      rangeStart = newRangeStart;
    }
  }
  return rangeStart;
}

function sliceDateRangeByTickUnit (dateRange: { lowerBound: Date; upperBound: Date }, unit: TickUnit) {
  const dates = [];
  let nextDate = dateRange.lowerBound;
  while (nextDate < dateRange.upperBound) {
    dates.push(nextDate);
    nextDate = add(nextDate, { [unit]: 1 });
  }
  return dates;
}
